import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCheck,
  ChevronDown,
  Download,
  Edit,
  FileDown,
  FileText,
  Lock,
  Mail,
  Send,
  Trash2,
  Truck,
  Undo2,
  X,
  ClipboardList,
  Factory,
  Package,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CurrencyTogglePill } from "@/features/purchase-orders/components/CurrencyTogglePill";
import { PaymentStatusBadge } from "@/features/purchase-orders/components/PaymentStatusBadge";
import { PoKindBadge } from "@/features/purchase-orders/components/PoKindBadge";
import type { PurchaseOrder } from "@/features/purchase-orders";
import type { BuyListPoKind } from "@threadheads/shared";
import { useOrganization } from "@/contexts/OrganizationContext";
import { cn } from "@/lib/utils";
import { STATUS_LABELS, STATUS_SELECT_CLASS } from "@/features/purchase-orders/utils/status-style";
import { formatDateTime, formatDay, useDateFnsLocale } from "@/lib/format-dates";

const STATUS_BADGE_VARIANTS: Record<PurchaseOrder["status"], "info" | "warning" | "default" | "processing" | "success" | "deleted"> = {
  draft: "info",
  submitted: "warning",
  in_production: "default",
  ready: "processing",
  in_transit: "default",
  partially_received: "processing",
  received: "success",
  cancelled: "deleted",
};

const STATUS_BADGE_ICONS: Record<PurchaseOrder["status"], typeof ClipboardList> = {
  draft: ClipboardList,
  submitted: ClipboardList,
  in_production: Factory,
  ready: Truck,
  in_transit: Truck,
  partially_received: Package,
  received: Package,
  cancelled: X,
};

interface StandardPoActionBarProps {
  order: PurchaseOrder;
  /** Absent on a standard PO or transfer: see `JustInTimePoActionBarProps`. */
  justInTime?: undefined;
  isTransfer: boolean;
  isAdmin: boolean;
  isPaid: boolean;
  // permissions
  canEdit: boolean;
  canSubmitDraft: boolean;
  canUndo: boolean;
  canSend: boolean;
  canCancel: boolean;
  canComplete: boolean;
  /**
   * Why Complete is unavailable, when the reason is one an operator can act on.
   * Non-null keeps the button on screen, disabled, wearing this text; null is
   * the old behaviour of simply not rendering it.
   */
  completeBlockedReason?: string | null;
  canDelete: boolean;
  // status select
  showStatusSelect: boolean;
  validStatuses: PurchaseOrder["status"][];
  onStatusChange: (next: PurchaseOrder["status"]) => void;
  // pending flags
  isUpdating: boolean;
  isUndoing: boolean;
  isSubmitting: boolean;
  isMarkingPaid: boolean;
  isCompleting: boolean;
  isExportingPdf: boolean;
  isSendingEmail: boolean;
  // actions
  onEdit: () => void;
  onSubmitDraft: () => void;
  onShipTransfer: () => void;
  onUndo: () => void;
  onComplete: () => void;
  onMarkPaid: () => void;
  onExportCsv: () => void;
  onDownloadPdf: () => void;
  onSend: () => void;
  onCancel: () => void;
  onDelete: () => void;
}

/**
 * BLU-1268: what a just-in-time PO's bar shows besides the shared chrome. The
 * page owns the actions (their state lives with its line edits); the bar owns
 * the order and the look: currency pill, read-only status in the page's words
 * (never the status Select, a just-in-time PO only completes or cancels), the
 * "Just in time" badge and the batch it was bought for.
 *
 * BLU-1506: an Order and wait PO is the other buy-list kind and rides the same
 * bar: `kind` swaps the kind badge, `statusStyle` gives the status badge its
 * own colour and glyph (Not sent yet / On the way / Late / Arrived) at the
 * same Badge geometry every PO bar draws, and `ariaLabel` names the bar.
 */
export interface JustInTimeActionBarContent {
  /** The buy-list kind the badge names; just in time when absent. */
  kind?: BuyListPoKind;
  /** The status in words ("To buy", "Completed", "Cancelled"; Order and wait: "On the way, due 28 Oct"). */
  statusLabel: string;
  /**
   * The badge's colours and glyph when they are not the PO status's own (an
   * Order and wait status: on the way is cyan, late amber). Same geometry
   * either way, so every PO bar's status badge reads alike.
   */
  statusStyle?: { className: string; icon: LucideIcon };
  /** The bar's accessible name; "Just in time order actions" when absent. */
  ariaLabel?: string;
  /** The batch link (or plain text when the viewer cannot open batches); null = none. */
  batch: ReactNode;
  /** A state beside the batch, e.g. "Batch cancelled". */
  notice?: ReactNode;
  /** Auxiliary then primary actions (DESIGN.md section 3.3 order). */
  actions?: ReactNode;
  /** The destructive action (Cancel), drawn after the divider. */
  destructive?: ReactNode;
}

interface JustInTimePoActionBarProps {
  order: PurchaseOrder;
  justInTime: JustInTimeActionBarContent;
}

type PoActionBarProps = StandardPoActionBarProps | JustInTimePoActionBarProps;

export function PoActionBar(props: PoActionBarProps) {
  if (props.justInTime) return <JustInTimePoActionBar order={props.order} content={props.justInTime} />;
  return <StandardPoActionBar {...props} />;
}

/**
 * The sticky chrome both bars share (DESIGN.md section 3): a sentinel whose
 * IntersectionObserver flips `data-stuck`, the translucent material, and the
 * two-cluster nav. Children are the left and right clusters.
 */
function PoActionBarShell({ ariaLabel, children }: { ariaLabel: string; children: ReactNode }) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setStuck(!entry.isIntersecting);
      },
      { rootMargin: "0px 0px 0px 0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <>
      {/* Sentinel — when this scrolls out of view, the bar is stuck */}
      <div ref={sentinelRef} aria-hidden className="h-px -mt-px" />

      <div
        className="sticky z-40"
        style={{ top: "var(--top-header-height, 3.5rem)" }}
        data-stuck={stuck || undefined}
      >
        <div
          className={cn(
            "relative -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8",
            "bg-background/85 supports-[backdrop-filter]:bg-background/65 backdrop-blur",
            "border-b border-transparent",
            "transition-[border-color,box-shadow] duration-200 motion-reduce:transition-none",
            "data-[stuck]:border-border/60 data-[stuck]:shadow-[0_1px_0_0_rgb(16_185_129_/_0.35),0_4px_12px_-6px_rgb(0_0_0_/_0.08)]",
          )}
          data-stuck={stuck || undefined}
        >
          <nav
            aria-label={ariaLabel}
            className="mx-auto flex max-w-screen-2xl flex-wrap items-end justify-between gap-x-4 gap-y-2 pt-3 pb-2.5"
          >
            {children}
          </nav>
        </div>
      </div>
    </>
  );
}

function JustInTimePoActionBar({ order, content }: { order: PurchaseOrder; content: JustInTimeActionBarContent }) {
  const { t } = useTranslation("orders");
  const StatusIcon =
    content.statusStyle?.icon ??
    (order.status === "received" ? CheckCheck : order.status === "cancelled" ? X : ClipboardList);
  const hasRight = Boolean(content.actions || content.destructive);

  return (
    <PoActionBarShell ariaLabel={content.ariaLabel ?? t("jit.bar.label")}>
      <div role="group" aria-label={t("jit.bar.state")} className="flex min-w-0 flex-wrap items-end gap-2">
        <CurrencyTogglePill
          poCurrency={order.currency}
          exchangeRate={order.exchange_rate}
          isLocked={order.payment_status === "paid"}
          lockedAt={order.paid_at ?? null}
          rateAbove
        />
        <CurrencyDividerSpacer poCurrency={order.currency} />
        <Badge
          variant={content.statusStyle ? "outline" : STATUS_BADGE_VARIANTS[order.status]}
          className={cn("flex h-8 items-center gap-1.5 px-3", content.statusStyle?.className)}
          role="status"
        >
          <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />
          {content.statusLabel}
        </Badge>
        <PoKindBadge kind={content.kind ?? "just_in_time"} size="bar" />
        {content.batch}
        {content.notice}
      </div>

      {hasRight && (
        <div className="ml-auto flex flex-wrap items-end justify-end gap-1.5">
          {content.actions}
          {content.actions && content.destructive && (
            <span aria-hidden className="mx-1 h-5 w-px self-end bg-border/60" />
          )}
          {content.destructive}
        </div>
      )}
    </PoActionBarShell>
  );
}

function StandardPoActionBar({
  order,
  isTransfer,
  isAdmin,
  isPaid,
  canEdit,
  canSubmitDraft,
  canUndo,
  canSend,
  canCancel,
  canComplete,
  completeBlockedReason,
  canDelete,
  showStatusSelect,
  validStatuses,
  onStatusChange,
  isUpdating,
  isUndoing,
  isSubmitting,
  isMarkingPaid,
  isCompleting,
  isExportingPdf,
  isSendingEmail,
  onEdit,
  onSubmitDraft,
  onShipTransfer,
  onUndo,
  onComplete,
  onMarkPaid,
  onExportCsv,
  onDownloadPdf,
  onSend,
  onCancel,
  onDelete,
}: StandardPoActionBarProps) {
  const dateLocale = useDateFnsLocale();

  const StatusIcon = STATUS_BADGE_ICONS[order.status];

  // Partner-portal acknowledgement (supplier portal). PR #1989 review: no cast,
  // the order type is the router output and the mapper returns this column.
  const supplierAcknowledgedAt = order.supplier_acknowledged_at ?? null;

  return (
    <PoActionBarShell ariaLabel="Purchase order actions">
      {/* LEFT — state cluster: currency leftmost, divider, then status + payment */}
      <div role="group" aria-label="Status and currency" className="flex min-w-0 items-end gap-2 flex-wrap">
        <CurrencyTogglePill
          poCurrency={order.currency}
          exchangeRate={order.exchange_rate}
          isLocked={isPaid}
          lockedAt={order.paid_at ?? null}
          rateAbove
        />

        {/* Vertical divider — only visible when CurrencyTogglePill renders something
            (it returns null for same-currency POs). Anchored to the pill row baseline
            so it visually separates the FX cluster from the status cluster. */}
        <CurrencyDividerSpacer poCurrency={order.currency} />

        {showStatusSelect ? (
          <Select value={order.status} onValueChange={(v) => onStatusChange(v as PurchaseOrder["status"])} disabled={isUpdating}>
            <SelectTrigger
              aria-label="Fulfillment status"
              className={cn(
                "w-[150px] h-8 border-0 text-[11px] font-medium",
                STATUS_SELECT_CLASS[order.status],
              )}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {validStatuses.map((status) => (
                <SelectItem key={status} value={status} className="text-[11px]">
                  {STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant={STATUS_BADGE_VARIANTS[order.status]} className="flex h-8 items-center gap-1.5 px-3">
            <StatusIcon className="h-3.5 w-3.5" aria-hidden="true" />
            {STATUS_LABELS[order.status]}
          </Badge>
        )}

        {!isTransfer && <PaymentStatusBadge paymentStatus={order.payment_status} />}

        {/* Supplier-portal acknowledgement — set once by the partner org
            via acknowledgePo; never cleared. */}
        {!isTransfer && supplierAcknowledgedAt && (
          <Badge
            variant="outline"
            className="flex h-8 items-center gap-1.5 px-3 border-emerald-200 bg-emerald-50 text-emerald-700"
            title={`Acknowledged by partner ${formatDateTime(supplierAcknowledgedAt, dateLocale)}`}
          >
            <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
            {/* PR #1989 review: shared date format, so the bar and the dates panel print the same day the same way. */}
            Acknowledged by partner · {formatDay(supplierAcknowledgedAt, dateLocale)}
          </Badge>
        )}
      </div>

      {/* RIGHT — action cluster */}
      <div className="flex flex-wrap items-end gap-1.5">
        {canEdit && (
          <ActionButton onClick={onEdit} icon={<Edit className="h-3.5 w-3.5" />}>
            Edit
          </ActionButton>
        )}

        {canSubmitDraft && (
          <Button
            size="sm"
            onClick={onSubmitDraft}
            disabled={isSubmitting}
            className="h-8 px-3 text-xs gap-1.5"
          >
            <Send className="h-3.5 w-3.5" />
            {isSubmitting ? "Submitting…" : isTransfer ? "Submit Transfer" : "Submit Order"}
          </Button>
        )}

        {isTransfer && order.status === "submitted" && (
          <Button
            size="sm"
            onClick={onShipTransfer}
            disabled={isUpdating}
            className="h-8 px-3 text-xs gap-1.5"
          >
            <Truck className="h-3.5 w-3.5" />
            Ship Transfer
          </Button>
        )}

        {canUndo && (
          <ActionButton
            onClick={onUndo}
            disabled={isUndoing}
            icon={<Undo2 className="h-3.5 w-3.5" />}
            className="border-amber-200 text-amber-700 hover:bg-amber-50"
          >
            Undo
          </ActionButton>
        )}

        {/* BLU-572: short-close a partially received PO. Emerald reads as
            the completed/received semantic (DESIGN.md §1) — a constructive
            terminal transition, mirroring Mark Paid's outline-emerald. */}
        {canComplete && (
          <ActionButton
            onClick={onComplete}
            disabled={isCompleting}
            icon={<CheckCheck className="h-3.5 w-3.5" />}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
          >
            {isCompleting ? "Completing…" : isTransfer ? "Complete transfer" : "Complete PO"}
          </ActionButton>
        )}

        {/* Blocked rather than unavailable: the action exists, something
            specific is in the way, and the operator can clear it. The
            span carries the hover because a disabled button has
            pointer-events: none, and it is focusable so the reason is
            reachable by keyboard as well as by mouse. */}
        {!canComplete && completeBlockedReason && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex rounded-md">
                <ActionButton
                  onClick={onComplete}
                  disabled
                  icon={<CheckCheck className="h-3.5 w-3.5" />}
                  className="border-emerald-200 text-emerald-700"
                >
                  {isTransfer ? "Complete transfer" : "Complete PO"}
                </ActionButton>
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="end" className="max-w-xs text-xs font-normal">
              {completeBlockedReason}
            </TooltipContent>
          </Tooltip>
        )}

        {!isTransfer && isAdmin && !isPaid && order.status !== "draft" && order.status !== "cancelled" && (
          <ActionButton
            onClick={onMarkPaid}
            disabled={isMarkingPaid}
            icon={<Lock className="h-3.5 w-3.5" />}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
          >
            {isMarkingPaid ? "Marking…" : "Mark Paid"}
          </ActionButton>
        )}

        {!isTransfer && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 px-3 text-xs gap-1.5">
                <Download className="h-3.5 w-3.5" />
                Export
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onExportCsv}>
                <FileText className="h-4 w-4 mr-2" />
                Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onDownloadPdf} disabled={isExportingPdf}>
                <FileDown className="h-4 w-4 mr-2" />
                Download PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {canSend && (
          <ActionButton
            onClick={onSend}
            disabled={isSendingEmail}
            icon={<Mail className="h-3.5 w-3.5" />}
          >
            Send to Supplier
          </ActionButton>
        )}

        {(canCancel || canDelete) && <span aria-hidden className="mx-1 h-5 w-px bg-border/60" />}

        {canCancel && (
          <ActionButton
            onClick={onCancel}
            icon={<X className="h-3.5 w-3.5" />}
            className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
          >
            Cancel
          </ActionButton>
        )}

        {canDelete && (
          <Button
            variant="destructive"
            size="sm"
            onClick={onDelete}
            className="h-8 px-3 text-xs gap-1.5"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
        )}
      </div>
    </PoActionBarShell>
  );
}

/**
 * Renders a vertical divider only when the CurrencyTogglePill is actually
 * shown (i.e. when po.currency differs from org base). Mirrors the same
 * conditional inside CurrencyTogglePill so the bar doesn't show a dangling
 * divider on same-currency POs.
 */
function CurrencyDividerSpacer({ poCurrency }: { poCurrency: string | null | undefined }) {
  const { organization } = useOrganization();
  const orgCurrency = organization?.default_currency ?? "AUD";
  const effective = poCurrency ?? orgCurrency;
  if (effective === orgCurrency) return null;
  return <span aria-hidden className="mx-1 h-6 w-px self-end bg-border/70" />;
}

function ActionButton({
  onClick,
  disabled,
  icon,
  className,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  icon: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className={cn("h-8 px-3 text-xs gap-1.5", className)}
    >
      {icon}
      {children}
    </Button>
  );
}
